import { InspectorControls } from '@wordpress/block-editor';
import {
	PanelBody,
	RangeControl,
	SelectControl,
	ToggleControl,
	__experimentalToggleGroupControl as ToggleGroupControl,
	__experimentalToggleGroupControlOption as ToggleGroupControlOption,
} from '@wordpress/components';
import { __ } from '@wordpress/i18n';

import useViewport from '../../../../hooks/useViewport';

/**
 * Each preset reads best at a different pace — a flip has further to travel
 * than a crossfade, an odometer less. Switching preset moves `duration` to the
 * matching default, unless the author has already set their own.
 */
export const PRESET_DURATIONS = {
	rise: 520,
	dissolve: 520,
	flip: 620,
	slide: 480,
};

const PRESET_OPTIONS = [
	{ value: 'rise', label: __( 'Rise', 'brandy-blocks' ) },
	{ value: 'dissolve', label: __( 'Dissolve', 'brandy-blocks' ) },
	{ value: 'flip', label: __( 'Flip', 'brandy-blocks' ) },
	{ value: 'slide', label: __( 'Slide', 'brandy-blocks' ) },
];

// Maps the editor's device preview onto the key the attribute stores.
const DEVICE_KEYS = {
	Desktop: 'desktop',
	Tablet: 'tablet',
	Mobile: 'mobile',
};

const DEVICE_LABELS = {
	desktop: __( 'Slots per row (desktop)', 'brandy-blocks' ),
	tablet: __( 'Slots per row (tablet)', 'brandy-blocks' ),
	mobile: __( 'Slots per row (mobile)', 'brandy-blocks' ),
};

export default function RotatorInspectorControls( {
	attributes,
	setAttributes,
} ) {
	const {
		interval,
		stagger,
		preset,
		alignment,
		duration,
		columns,
		pauseOnHover,
	} = attributes;

	const { viewport } = useViewport();
	const device = DEVICE_KEYS[ viewport ] || 'desktop';

	const setPreset = ( value ) => {
		const wasDefault = duration === PRESET_DURATIONS[ preset ];

		setAttributes( {
			preset: value,
			// Only follow the preset while the author has not taken the wheel.
			...( wasDefault ? { duration: PRESET_DURATIONS[ value ] } : {} ),
		} );
	};

	const setColumns = ( value ) =>
		setAttributes( {
			columns: { ...columns, [ device ]: value },
		} );

	return (
		<InspectorControls>
			<PanelBody title={ __( 'Rotation', 'brandy-blocks' ) }>
				<SelectControl
					label={ __( 'Transition', 'brandy-blocks' ) }
					value={ preset }
					options={ PRESET_OPTIONS }
					onChange={ setPreset }
					__nextHasNoMarginBottom
				/>

				{ /*
				  * Where each item sits inside its slot. The slots themselves
				  * stay evenly spread — that is what keeps the row from
				  * shifting when a wide item swaps for a narrow one.
				  */ }
				<ToggleGroupControl
					label={ __( 'Alignment', 'brandy-blocks' ) }
					value={ alignment || 'center' }
					onChange={ ( value ) =>
						setAttributes( { alignment: value } )
					}
					isBlock
					__nextHasNoMarginBottom
				>
					<ToggleGroupControlOption
						value="left"
						label={ __( 'Left', 'brandy-blocks' ) }
					/>
					<ToggleGroupControlOption
						value="center"
						label={ __( 'Center', 'brandy-blocks' ) }
					/>
					<ToggleGroupControlOption
						value="right"
						label={ __( 'Right', 'brandy-blocks' ) }
					/>
				</ToggleGroupControl>

				<RangeControl
					label={ __( 'Time between changes', 'brandy-blocks' ) }
					/*
					 * Measured from one change to the next, not the pause
					 * between them — at the defaults the row is still for
					 * about 2.4 of every 3.5 seconds. Without saying so, this
					 * reads as the pause and gets set far too low.
					 */
					help={ __(
						'Measured from the start of one change to the start of the next, so it includes the change itself.',
						'brandy-blocks'
					) }
					value={ interval }
					onChange={ ( value ) => setAttributes( { interval: value } ) }
					min={ 1000 }
					max={ 10000 }
					step={ 250 }
					__nextHasNoMarginBottom
				/>

				<RangeControl
					label={ __( 'Transition duration', 'brandy-blocks' ) }
					value={ duration }
					onChange={ ( value ) => setAttributes( { duration: value } ) }
					min={ 200 }
					max={ 1200 }
					step={ 20 }
					__nextHasNoMarginBottom
				/>

				<RangeControl
					label={ __( 'Stagger', 'brandy-blocks' ) }
					help={ __(
						'Delay between each slot, left to right. 0 swaps them all at once.',
						'brandy-blocks'
					) }
					value={ stagger }
					onChange={ ( value ) => setAttributes( { stagger: value } ) }
					min={ 0 }
					max={ 400 }
					step={ 10 }
					__nextHasNoMarginBottom
				/>

				<RangeControl
					label={ DEVICE_LABELS[ device ] }
					help={ __(
						'Extra slots wrap onto the next row.',
						'brandy-blocks'
					) }
					value={ columns?.[ device ] ?? 5 }
					onChange={ setColumns }
					min={ 1 }
					max={ 8 }
					__nextHasNoMarginBottom
				/>

				<ToggleControl
					label={ __( 'Pause on hover', 'brandy-blocks' ) }
					checked={ !! pauseOnHover }
					onChange={ ( value ) =>
						setAttributes( { pauseOnHover: value } )
					}
					__nextHasNoMarginBottom
				/>
			</PanelBody>
		</InspectorControls>
	);
}
